import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";
import { oscillatorWave } from "../engine/drivers.js";

/**
 * OscillatorNode
 * --------------
 * Oscillates a numeric value between min and max.
 * Supports multiple waveforms: sine, square, triangle, saw.
 */
export class OscillatorNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Oscillator";

    this.properties = {
      min: 0,
      max: 1,
      frequency: 1, // Hz
      waveform: "sine",
    };

    this._phase = 0;

    // Output
    this.addOutput("Value", "number");

    // Waveform combo (non-drivable)
    this.addWidget("combo", "Waveform", this.properties.waveform, (val) => {
      this.properties.waveform = val;
    }, {
      values: ["sine", "square", "triangle", "saw"]
    });

    // Drivable sliders — connect other oscillators or values to drive these
    addDrivableSlider(this, "Min", "min", this.properties.min, -500, 500, 0.1);
    addDrivableSlider(this, "Max", "max", this.properties.max, -500, 500, 0.1);
    addDrivableSlider(this, "Freq (Hz)", "frequency", this.properties.frequency, 0.01, 20, 0.01);

    // 0 visible inputs — all inputs are drivable
    finalizeDrivableSliders(this, 0);

    this.size = [200, 130];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  _wave(t) {
    return oscillatorWave(t, this.properties.waveform);
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const curveY = h - 25;
    const curveH = 18;
    const pad = 10;

    // Draw waveform preview
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= w - pad * 2; i++) {
      const t = i / (w - pad * 2);
      const v = this._wave(t);
      const y = curveY - v * curveH;
      i === 0 ? ctx.moveTo(pad + i, y) : ctx.lineTo(pad + i, y);
    }
    ctx.stroke();

    // Draw current position dot
    const cyclePos = this._phase % 1;
    const dotX = pad + cyclePos * (w - pad * 2);
    const dotY = curveY - this._wave(cyclePos) * curveH;
    ctx.fillStyle = "#4E9EFF";
    ctx.beginPath();
    ctx.arc(dotX, dotY, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  onExecute() {
    syncDrivableSliders(this);

    const dt = 1 / 60;
    this._phase += this.properties.frequency * dt;
    if (this._phase > 1000) this._phase -= 1000;

    const cyclePos = this._phase % 1;
    const wave = this._wave(cyclePos);
    const { min, max } = this.properties;
    const value = min + wave * (max - min);

    this.setOutputData(0, value);
    this.setDirtyCanvas(true);
  }
}

LiteGraph.registerNodeType("generators/Oscillator", OscillatorNode);
